'use client';

import React from 'react';
import styled from 'styled-components';
import Link from 'next/link';
import { COLORS } from '@/constants/colors';

interface SupportSectionProps {
  text?: string;
  primaryButtonText?: string;
  primaryButtonLink?: string;
  secondaryButtonText?: string;
  secondaryButtonLink?: string;
}

const SupportSection: React.FC<SupportSectionProps> = ({
  text = 'Need help finding the right filter? Use our vehicle selector or contact our support team for expert assistance.',
  primaryButtonText = 'Shop by Vehicle',
  primaryButtonLink = '/shop-by-vehicle',
  secondaryButtonText = 'Contact Support',
  secondaryButtonLink = '/contact-us',
}) => {
  return (
    <CTASection>
      <Container>
        <CTAContent>
          <CTAText>{text}</CTAText>
          <CTAButtonGroup>
            <CTAButton href={primaryButtonLink}>{primaryButtonText}</CTAButton>
            <CTAButtonSecondary href={secondaryButtonLink}>{secondaryButtonText}</CTAButtonSecondary>
          </CTAButtonGroup>
        </CTAContent>
      </Container>
    </CTASection>
  );
};

export default SupportSection;

// Styled Components
const CTASection = styled.section`
  background: linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%);
  padding: 60px 24px;

  @media (max-width: 767px) {
    padding: 40px 16px;
  }
`;

const Container = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 20px;
`;

const CTAContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
`;

const CTAText = styled.p`
  font-family: Lato, sans-serif;
  font-size: 18px;
  font-weight: 400;
  color: ${COLORS.white};
  margin: 0 0 32px;
  line-height: 1.6;

  @media (max-width: 767px) {
    font-size: 16px;
    margin: 0 0 24px;
  }
`;

const CTAButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;

  @media (max-width: 767px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const CTAButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 14px 32px;
  background: ${COLORS.white};
  color: ${COLORS.primary};
  font-family: Lato, sans-serif;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  border-radius: 8px;
  transition: all 0.3s ease;
  border: 2px solid ${COLORS.white};

  &:hover {
    background: transparent;
    color: ${COLORS.white};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 767px) {
    width: 100%;
    padding: 12px 24px;
  }
`;

const CTAButtonSecondary = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 14px 32px;
  background: transparent;
  color: ${COLORS.white};
  font-family: Lato, sans-serif;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  border-radius: 8px;
  transition: all 0.3s ease;
  border: 2px solid ${COLORS.white};

  &:hover {
    background: ${COLORS.white};
    color: ${COLORS.primary};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 767px) {
    width: 100%;
    padding: 12px 24px;
  }
`;
